import {
  artifactUserRevisionAtom,
  openArtifactAtom,
  type Artifact,
} from '@heidi/agents/data';
import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';

import { resolveWorkBillingAnalyticsProps } from '@/models/analytics/features/work';
import { WorkLaunchEventName, trackWorkEvent } from '@heidi/agents/data';

/**
 * Emits work_artifact_actioned for user-driven open / close of the canvas.
 * Ignores silent agent auto-opens (they do not bump artifactUserRevisionAtom).
 */
export function useTrackWorkArtifactCanvasActions(projectId?: string): void {
  const artifact = useAtomValue(openArtifactAtom);
  const userRevision = useAtomValue(artifactUserRevisionAtom);
  const prevRevisionRef = useRef(userRevision);
  const prevArtifactRef = useRef<Artifact | null>(artifact);

  useEffect(() => {
    if (userRevision === prevRevisionRef.current) return;
    prevRevisionRef.current = userRevision;

    const previous = prevArtifactRef.current;
    prevArtifactRef.current = artifact;
    const billing = resolveWorkBillingAnalyticsProps();
    const base = {
      object_type: 'artifact' as const,
      surface: 'project_chat' as const,
      ...(projectId ? { project_id: projectId } : {}),
      ...billing,
    };

    if (artifact == null && previous != null) {
      trackWorkEvent(WorkLaunchEventName.WorkArtifactActioned, {
        ...base,
        action: 'closed',
        artifact_type: previous.type,
      });
      return;
    }

    if (artifact != null) {
      trackWorkEvent(WorkLaunchEventName.WorkArtifactActioned, {
        ...base,
        action: 'opened',
        artifact_type: artifact.type,
      });
    }
  }, [artifact, userRevision, projectId]);
}
